import { Pacient, IPacient } from "app/models/pacient";
import {
  fGetFunction,
  fPostFunction,
  getAPIAuthToken,
  getAPIFunctionEndpoint,
} from "./firebase";

const baseApiUrl = "pacients";

export function getList(nameIndex: string, pageSize: number) {
  return fGetFunction(
    `${baseApiUrl}/get-list?nameIndex=${nameIndex}&pageSize=${pageSize}`
  );
}

export function getByName(name: string) {
  return fGetFunction(`${baseApiUrl}/get-by-name?name=${name}`);
}

export async function getDeletedByCpf(cpf: string): Promise<Pacient | null> {
  return fGetFunction(
    `${baseApiUrl}/get-deleted-by-cpf?cpf=${cpf}`
  ).then((r: IPacient) => (r ? new Pacient(r) : null));
}

export function getPatientsWithOpenMonitorings(sectorFilter: Array<string>) {
  return fGetFunction(
    `${baseApiUrl}/get-patients-with-open-monitorings?sectorFilter=${sectorFilter}`
  );
}

export function getPatientsWithFilterSector(sectorFilter: Array<string>) {
  return fGetFunction(
    `${baseApiUrl}/get-patients-with-filter-sector?sectorFilter=${sectorFilter}`
  );
}

export function getAvailableList(): Promise<Array<Pacient>> {
  return fGetFunction(`${baseApiUrl}/get-available-list`).then((r) =>
    r.map((v: IPacient) => new Pacient(v))
  );
}

export function searchAvailable(term: string): Promise<Array<Pacient>> {
  return fGetFunction(
    `${baseApiUrl}/search-available?q=${encodeURIComponent(term)}`
  ).then((r) => (r ?? []).map((v: IPacient) => new Pacient(v)));
}

export function getItem(id: string): Promise<Pacient> {
  return fGetFunction(`${baseApiUrl}/get-item`, {
    id: id,
  }).then((r) => new Pacient(r));
}

export function create(data: any) {
  return fPostFunction(`${baseApiUrl}/add-item`, data);
}

export function update(data: any) {
  return fPostFunction(`${baseApiUrl}/update-item`, data);
}

export function remove(id: string) {
  return fPostFunction(`${baseApiUrl}/delete-item`, {
    id: id,
  });
}

export interface GetAllPatientsFilters {
  name?: string;
  limit?: number;
  offset?: number;
}

export interface GetAllPatientsWithFiltersResponse {
  patients: Pacient[];
  total: number;
}

export async function getAllPatientsWithFilters(
  filters: GetAllPatientsFilters
): Promise<GetAllPatientsWithFiltersResponse> {
  const response = await fPostFunction(
    `${baseApiUrl}/get-all-filters`,
    filters
  );
  return response;
}

// === Profile Photo

// ! Notice, base64 is not recommended for large files, as it increases the size of the data being sent over the network.
// However, an approach using FormData was not working with Firebase functions, so we are using base64 for now.
export async function updatePatientProfilePhoto(
  patientId: string,
  profilePhoto: File
): Promise<string> {
  const token = await getAPIAuthToken();
  const endpoint = getAPIFunctionEndpoint(`${baseApiUrl}/update-profile-photo`);

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  const base64Photo = await toBase64(profilePhoto);
  const photoExtension = profilePhoto.type.split("/")[1];

  const payload = {
    patientId,
    profilePhoto: base64Photo,
    photoExtension,
  };

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Failed to update profile photo");
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.message || "Failed to update profile photo");
  }

  return data.photoUrl;
}

export async function deletePatientProfilePhoto(
  patientId: string,
  photoExtension: string
): Promise<void> {
  const token = await getAPIAuthToken();
  const endpoint = getAPIFunctionEndpoint(`${baseApiUrl}/delete-profile-photo`);

  const payload = {
    patientId,
    photoExtension,
  };

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error("Failed to delete profile photo");
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.message || "Failed to delete profile photo");
  }
}
